import { useEffect } from "react";
import * as Sentry from "@sentry/react";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase";
import { useAuthStore } from "@/stores/auth";
import { useAdminOrgStore } from "@/stores/admin-org";
import { useWasteFiltersStore } from "@/stores/waste-filters";
import { queryClient } from "@/lib/query-client";
import { setLastLocale } from "@/i18n/last-locale";
import { getAppMetadata } from "@/lib/jwt";
import { identifyUser, resetUser } from "@/lib/posthog";
import { API_URL } from "@/lib/admin-api";

const COOKIE_DOMAIN = window.location.hostname.endsWith(".foodsight.com")
  ? "; domain=.foodsight.com"
  : "";

function setSignedInCookie() {
  document.cookie = `fs_signed_in=1${COOKIE_DOMAIN}; path=/; SameSite=Lax; max-age=31536000`;
}

function clearSignedInCookie() {
  document.cookie = `fs_signed_in=${COOKIE_DOMAIN}; path=/; SameSite=Lax; max-age=0`;
}

function syncSentryUser(session: Session | null) {
  if (!session) {
    Sentry.setUser(null);
    Sentry.setTag("org_id", null);
    Sentry.setTag("role", null);
    Sentry.setTag("is_platform_admin", null);
    return;
  }
  Sentry.setUser({ id: session.user.id, email: session.user.email });
  const claims = getAppMetadata(session.access_token);
  Sentry.setTag("org_id", (claims?.org_id as string) ?? null);
  Sentry.setTag("role", (claims?.role as string) ?? null);
  Sentry.setTag("is_platform_admin", Boolean(claims?.is_platform_admin));
}

// Identify the signed-in user in PostHog by email (POS2-940); reset on sign-out so
// later events aren't attributed to the previous user. No-ops without a key.
function syncPosthogUser(session: Session | null) {
  if (session) identifyUser(session.user.id, session.user.email ?? undefined);
  else resetUser();
}

// Which user this tab has already told the backend about, **and on which day**. The backend writes
// one login row per user per civil day under a unique index, so this guard is an optimisation and
// not the guarantee: a session that is never signed out fires TOKEN_REFRESHED roughly hourly, and
// without it every tab would POST on every refresh for a row that already exists.
//
// **The day is the load-bearing half.** Keyed on the user id alone, a pinned tab pings once and then
// suppresses every refresh for as long as it stays open — `sessionStorage` survives reloads and
// `INITIAL_SESSION` does not ping — so a user who lives in one tab is recorded on day one and never
// again. They then accrue `login_gap_days` and eventually churn points: the exact failure decision 10
// emits on TOKEN_REFRESHED to prevent, and a direct contradiction of "a user who leaves the app open
// for a week and never signs out is not reported as a week quiet".
//
// The browser's local date is fine as the key. The authority is the unique index on the
// *organisation's* civil day, so at a zone boundary the two can disagree — and the disagreement
// costs one extra POST that the index swallows. Erring toward pinging is the safe direction; erring
// toward suppressing is the bug above.
const LOGIN_PINGED_KEY = "fs_login_pinged";

function pingedToday(userId: string): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${userId}:${now.getFullYear()}-${month}-${day}`;
}

/**
 * Tell the backend this user opened the app (POS2-1138 decision 10).
 *
 * Fired on SIGNED_IN **and** TOKEN_REFRESHED, deliberately. A Supabase session refreshes silently
 * forever, so a sign-in-only event would report a daily user as thirty days quiet and put their
 * site on the churn list. The backend takes the actor from the JWT and never from this call.
 *
 * Fire and forget: this is telemetry about the session, not part of it, and a failed ping must
 * never be something the user sees. The catch is what makes that true offline.
 */
function pingLogin(session: Session, trigger: "signed_in" | "token_refreshed") {
  try {
    const key = pingedToday(session.user.id);
    if (sessionStorage.getItem(LOGIN_PINGED_KEY) === key) return;
    sessionStorage.setItem(LOGIN_PINGED_KEY, key);
  } catch {
    // Private browsing, or storage disabled. Ping anyway — the unique index still holds.
  }
  void fetch(`${API_URL}/api/v1/activity/login`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({ trigger }),
  }).catch(() => {});
}

export function useAuth() {
  const { session, user, isLoading, setSession, setLoading } = useAuthStore();

  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      syncSentryUser(session);
      syncPosthogUser(session);

      if (event === "SIGNED_IN" || event === "TOKEN_REFRESHED") {
        setSignedInCookie();
        if (session) pingLogin(session, event === "SIGNED_IN" ? "signed_in" : "token_refreshed");
      } else if (event === "SIGNED_OUT") {
        clearSignedInCookie();
        // The org/site switcher (localStorage) and the shared waste filters (sessionStorage)
        // outlive the session, so without this the next person to sign in on this browser
        // inherits the last one's org and site — scoping their dashboard to a site their own
        // org need not even contain, which renders as an empty period rather than an error
        // (POS2-1068).
        useAdminOrgStore.getState().clearOrg();
        useWasteFiltersStore.getState().clearShared();
        // The same defect one layer down (POS2-1133). The query cache outlives the session too,
        // and the two self-scoped preference reads are keyed on the endpoint rather than on the
        // user — `["user-locale"]` and `["dashboard-layout"]` both call
        // /users/me/preferences — so without this the next person to sign in on this browser
        // opens the app in the previous one's language, with the previous one's dashboard order,
        // until each refetch lands.
        setLastLocale(null);
        queryClient.clear();
        // Alongside the clears above, and for the same reason: without it the next person to sign
        // in on this browser inherits the previous one's guard and their first login of the day
        // goes unrecorded.
        try {
          sessionStorage.removeItem(LOGIN_PINGED_KEY);
        } catch {
          // Nothing to clear if storage was never available.
        }
      }

      // INITIAL_SESSION fires after PKCE code exchange completes (if any).
      // This avoids the race where getSession() resolves before the
      // OAuth redirect code is exchanged, causing a premature redirect to /login.
      if (event === "INITIAL_SESSION") {
        if (session) setSignedInCookie();
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, [setSession, setLoading]);

  return { session, user, isLoading };
}
